/**
 * The protocol client.
 *
 * One battle-tested reconnect/replay/idempotency implementation, not several
 * (deep-dive 2.5): the bidder UI, the clerk console, and the load harness all
 * ride this exact code, so the thing that gets exercised by 5,000 simulated
 * connections is the thing real bidders use.
 *
 * Guarantees it upholds on behalf of the UI:
 *  - Events render strictly in seq order; duplicates are discarded.
 *  - A gap in the wire's event stream is never papered over: the client stops
 *    applying events, asks the ring for a fresh snapshot (RESYNC), and resumes
 *    from that truth (ADR-60, brief section 89).
 *  - A submitted bid never resolves to silence: every bid() settles with the
 *    engine's outcome, surviving reconnects via its idempotency key.
 *  - Floor-input roles heartbeat automatically; a dead console must look dead
 *    to the engine (spec 9.1), so the heartbeat stops the moment close() runs.
 */

import { PROTOCOL_VERSION, RING_NOT_ASSIGNED_CLOSE_REASON } from '@hipnumber/protocol';
import type { BidderIntentSignal, InterestFrame, IntentShown, MaxBidsFrame } from '@hipnumber/protocol';

export type { BidderIntentSignal, InterestFrame, IntentShown, MaxBidsFrame };

export { LiveBidderStore } from './store.js';
export type {
  BidButtonState,
  BidderLiveState,
  ConnectionWord,
  LiveBidderView,
  RequiredAck,
  StoreEventResult,
  YouStanding,
} from './store.js';

/** The subset of the WebSocket surface the client needs. Satisfied by the
 *  browser WebSocket and by the `ws` package in Node. */
export interface WebSocketLike {
  onopen: ((ev?: unknown) => void) | null;
  onmessage: ((ev: { data: unknown }) => void) | null;
  onclose: ((ev?: unknown) => void) | null;
  onerror: ((ev?: unknown) => void) | null;
  readyState: number;
  send(data: string): void;
  close(): void;
}

export type Role = 'clerk' | 'spotter' | 'bidder' | 'observer';

export interface Snapshot {
  seq: number;
  lotId: string | null;
  lotState: string;
  currentHighBid: number | null;
  currentAsk: number | null;
  highBidderRef: string | null;
  onlineStatus: 'OPEN' | 'PAUSED';
  pauseReason: string | null;
  /** Non-null once the ring is finalized (FINALIZE_SALE). A floor client that
   *  sees this stops heartbeating and reconnecting - the reducer now rejects
   *  every command, so a heartbeat is only noise. */
  finalizedSeq?: number | null;
  /** ADR-64: the ring's most recent disposition (null until one has
   *  happened; absent from rings older than the field). */
  previousLot?: { lotId: string; outcome: 'SOLD' | 'NO_SALE' | 'PASSED' | 'VOIDED'; price: number | null; seq: number; at: number } | null;
  [extra: string]: unknown;
}

export interface Outcome {
  idempotencyKey: string;
  accepted: boolean;
  seq?: number;
  reason?: string;
  /** ADR-60 cut 2: HELD for the operator; a BID_RESOLUTION follows. */
  pending?: boolean;
  pendingBidId?: string;
}

/** ADR-60 cut 2: how a held bid ended (private to its bidder). */
export interface BidResolution {
  pendingBidId: string;
  idempotencyKey?: string;
  result: 'ACCEPTED' | 'REJECTED' | 'SUPERSEDED_BY_FLOOR' | 'ASK_CHANGED' | 'LOT_CLOSED' | 'EXPIRED';
  seq: number;
  acceptedSeq?: number;
}

export type ConnectionStatus = 'connecting' | 'connected' | 'reconnecting' | 'closed';

/** ADR-70 cut 3: consecutive 4401 closes (no snapshot between them) a client
 *  with a re-minting token rides out before it stops and says so. */
export const MAX_AUTH_RETRIES = 5;

/** ADR-60 cut 7: a support command as the page sees it. */
export interface SupportCommand {
  commandId: string;
  command: string;
  reason: string;
  incidentNumber?: string;
}
export interface SupportOutcome {
  status: 'SUCCEEDED' | 'FAILED';
  summary?: string;
  diagnostics?: Record<string, unknown>;
}

/** Brief section 32: what the client knows about its own connection. Plain
 *  facts; the words a bidder sees are derived in the store. */
export interface ClientConnectionHealth {
  websocketConnected: boolean;
  lastHeartbeatAt: number | null;
  lastRoundTripMs: number | null;
  reconnectCount: number;
  connectedDurationMs: number;
  lastDisconnectReason: string | null;
  /** When the last authoritative snapshot arrived. */
  lastSnapshotAt: number | null;
  /** Gaps detected on the event stream (each one cost a RESYNC). */
  gapCount: number;
  /** A RESYNC is outstanding: events are being held until the snapshot lands. */
  resyncing: boolean;
  /** ADR-70: the screen may be trusted to act on - the socket is open, the
   *  ring has answered this connection with a snapshot, and no resync is
   *  outstanding. A console disables sell/pass-class controls while false. */
  ringLive: boolean;
  /** ADR-70: frames dropped because they named a ring other than the one this
   *  client pinned on its first snapshot. Anything above zero is a routing
   *  fault, never a client quirk. */
  ringMismatches: number;
}

/** ADR-70: why a resync was asked for (the ring counts them by cause). */
export type ResyncReason = 'GAP' | 'PONG' | 'MANUAL' | 'SUPPORT';

/**
 * ADR-70: how one floor command ended, from the console's side. floor()
 * always settles with one of these and never throws.
 *
 *   ACCEPTED  the ring applied it (or had already: `duplicate`).
 *   REJECTED  the ring refused it; `reason` says why. STALE_AUCTION_STATE
 *             means the screen was behind the ring - a fresh snapshot is
 *             already on its way.
 *   NOT_SENT  refused here, before the wire: NOT_CONNECTED (no socket, no
 *             HELLO or no snapshot yet on this connection) or RESYNCING.
 *   SENT      an older ring that does not answer floor commands: sent, as
 *             before; a refusal still arrives as an ERROR frame.
 *   TIMEOUT   no answer in time. The ring may or may not have applied it;
 *             the board shows which.
 */
export interface FloorCommandResult {
  status: 'ACCEPTED' | 'REJECTED' | 'NOT_SENT' | 'SENT' | 'TIMEOUT';
  accepted: boolean;
  commandType: string;
  /** The id the command went out under; null when it never left. */
  clientActionId: string | null;
  reason?: string;
  seq?: number;
  duplicate?: boolean;
  /** The ring's last seq when it answered. */
  authoritativeSeq?: number;
}

export interface ProtocolClientOptions {
  url: string;
  actorId: string;
  role: Role;
  /** ADR-33: proves who you are. A function so a long-lived client can hand
   *  the server a FRESH access token on every (re)connect - the page keeps
   *  it in memory and re-mints it from the session cookie. Dev rings take
   *  dev:<id>:<role>. Observers may omit it. */
  token?: string | (() => string | undefined | Promise<string | undefined>);
  /** This device's bidding session id (ADR-60). Recorded by the ring now;
   *  the fencing token in cut 2. Defaults to a per-client random id. */
  sessionId?: string;
  /** ADR-70 cut 6b (hitlist 8.2): the surface this client is (WEB_LIVE for
   *  the sale page, WEB_CATALOG for a timed catalog). Sent in HELLO; the ring
   *  decides what it keeps. Absent: the ring records the device only. */
  origin?: string;
  /** ADR-70 cut 6b: the window the bidder is looking at, read when a bid or
   *  a max is placed and sent with it as evidence. Absent or throwing: the
   *  bid goes without it. */
  viewport?: () => { w: number; h: number } | undefined;
  /** Defaults to globalThis.WebSocket; inject `ws` in Node. */
  webSocketFactory?: (url: string) => WebSocketLike;
  onSnapshot?: (snapshot: Snapshot) => void;
  /** `prevSeq` is the previous seq this wire carried (ADR-60); the client has
   *  already verified there is no gap before calling this. */
  onEvent?: (event: Record<string, unknown>, seq: number) => void;
  /** ADR-60: a personal standing update (YOU frame). */
  onYou?: (you: Record<string, unknown>) => void;
  /** ADR-60 cut 2: a held bid of ours ended (BID_RESOLUTION frame). */
  onResolution?: (resolution: BidResolution) => void;
  onStatus?: (status: ConnectionStatus) => void;
  onLatency?: (roundTripMs: number) => void;
  /** ADR-60: connection facts changed (a ping answered, a reconnect, a gap). */
  onHealth?: (health: ClientConnectionHealth) => void;
  /** Server ERROR frames: a rejected floor command, an unauthorized HELLO, a
   *  malformed frame. A console must SHOW these - silence is the enemy.
   *  `detail` carries the frame's machine-readable reason when it has one
   *  (ADR-70 cut 5f: RING_NOT_ASSIGNED and the rings this staffer runs). */
  onError?: (message: string, detail?: { readonly code: string; readonly rings?: readonly string[] }) => void;
  /** ADR-70 cut 6e (hitlist 9.2): what a VIEW_MAX_BIDS showed this screen.
   *  Only the socket that asked gets it; fields outside the viewer's scopes
   *  are absent, never blank. */
  onMaxBids?: (frame: MaxBidsFrame) => void;
  /** ADR-70 cut 6f (hitlist 10.2): the Online interest panel. Only a clerk's
   *  console gets it, and only while the tenant's switch is on. */
  onInterest?: (frame: InterestFrame) => void;
  /**
   * ADR-60 cut 7: a support command the client itself cannot carry out
   * (RELOAD_APPLICATION, RESTART_MEDIA_PLAYER, TEST_CAMERA, ...). The page
   * returns how it went; the client answers the ring. Commands the client
   * can do alone (refresh, reconnect, diagnostics, device info) are handled
   * here and never reach this hook. Absent: such commands FAIL with
   * "not supported by this client", never silently.
   */
  onSupport?: (command: SupportCommand) => SupportOutcome | Promise<SupportOutcome>;
  /** ADR-60 cut 7: extra sanitized facts for the diagnostic bundle (app
   *  version, media status, screen). Never a token, never payment data. */
  diagnostics?: () => Record<string, string | number | boolean | null>;
  pingIntervalMs?: number;
  heartbeatIntervalMs?: number;
  /** How long a bid may remain unresolved across reconnects before its promise
   *  rejects. The engine's outcome is still immutable and durable server-side;
   *  this bounds how long the UI waits, never what the truth is. */
  bidTimeoutMs?: number;
  /** If no outcome arrives within this window the connection is presumed
   *  half-open and force-reconnected (re-sending the bid idempotently). */
  bidNudgeMs?: number;
  /** How long a RESYNC may go unanswered before the socket is presumed dead
   *  and reconnected (which yields a snapshot anyway). */
  resyncTimeoutMs?: number;
  /** ADR-70: how long floor() waits for the ring's COMMAND_RESULT (across
   *  reconnects, re-sent under the same id) before settling TIMEOUT. */
  floorTimeoutMs?: number;
  /** Injected clock, for tests. */
  now?: () => number;
}

/** ADR-70: a keyed floor command awaiting its COMMAND_RESULT. */
interface PendingFloor {
  frame: { type: 'FLOOR_COMMAND'; clientActionId: string; command: Record<string, unknown> };
  resolve: (result: FloorCommandResult) => void;
  timer: ReturnType<typeof setTimeout>;
}

interface PendingBid {
  /** A keyed bidder intent: a bid, or (ADR-42) a max placement / cancel. All
   *  resolve to exactly one OUTCOME by idempotencyKey. */
  frame:
    | { type: 'PLACE_BID'; lotId: string; atAsk: number; idempotencyKey: string; viewport?: { w: number; h: number } }
    | { type: 'PLACE_MAX_BID'; lotId: string; maxAmount: number; idempotencyKey: string; viewport?: { w: number; h: number } }
    | { type: 'CANCEL_MAX_BID'; lotId: string; idempotencyKey: string };
  resolve: (outcome: Outcome) => void;
  reject: (err: Error) => void;
  timer: ReturnType<typeof setTimeout>;
  nudge: ReturnType<typeof setTimeout>;
}

const OPEN = 1;

/** A per-client session id when the page did not supply one. Not a secret:
 *  the ring binds it to the authenticated actor at HELLO. */
/**
 * 128 bits of randomness for a keyed intent (post-merge audit): a UUID v4
 * where the platform has one, else sixteen bytes from Web Crypto as hex.
 * Only a platform with NO Web Crypto at all (none we target) falls back to
 * Math.random, and even then to 128 bits of it.
 */
function randomSuffix(): string {
  const g = globalThis as { crypto?: { getRandomValues?: (a: Uint8Array) => Uint8Array; randomUUID?: () => string } };
  if (g.crypto?.randomUUID) return g.crypto.randomUUID();
  if (g.crypto?.getRandomValues) {
    const b = g.crypto.getRandomValues(new Uint8Array(16));
    return Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
  }
  let out = '';
  for (let i = 0; i < 4; i++) out += Math.floor(Math.random() * 0x100000000).toString(16).padStart(8, '0');
  return out;
}

function randomSessionId(): string {
  const g = globalThis as { crypto?: { randomUUID?: () => string } };
  if (g.crypto?.randomUUID) return g.crypto.randomUUID();
  return 'sess-' + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

export class ProtocolClient {
  private socket: WebSocketLike | null = null;
  private closed = false;
  /** Reset by the ring's first SNAPSHOT on a socket, not by the socket
   *  opening (ADR-70 cut 3): a ring that accepts the socket and then closes
   *  it would otherwise be retried every 250 ms forever. */
  private backoffMs = 250;
  /** ADR-70 cut 3: 4401 closes since the ring last answered with a snapshot. */
  private authFailures = 0;
  private lastSeq = 0;
  private lastInboundAt: number;
  private readonly pending = new Map<string, PendingBid>();
  private readonly timers: ReturnType<typeof setInterval>[] = [];
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  /** Set once the ring is finalized: the floor heartbeat is stopped and no
   *  reconnect is scheduled, because the sealed ring rejects every command. */
  private finalized = false;
  private bidCounter = 0;
  private readonly now: () => number;
  readonly sessionId: string;

  // ---- ADR-60 health facts ----
  private connected = false;
  private connectedAt: number | null = null;
  private lastRttMs: number | null = null;
  private lastPongAt: number | null = null;
  private reconnectCount = 0;
  private lastDisconnectReason: string | null = null;
  /** Why the client itself is closing the socket, if it is; read by onclose. */
  private closeReason: string | null = null;
  private lastSnapshotAt: number | null = null;
  private gapCount = 0;
  private resyncTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly onVisible: (() => void) | null = null;

  // ---- ADR-70 ----
  /** HELLO has gone out on the current socket. */
  private helloSent = false;
  /** The ring has answered the current socket with a snapshot. */
  private snapshotOnSocket = false;
  /** The latest snapshot said the ring answers keyed floor commands. */
  private commandResults = false;
  /** The ring and sale this client belongs to, pinned on the first snapshot
   *  that names them. */
  private pinnedRing: { ringId: string; saleId: string | null } | null = null;
  private ringMismatches = 0;
  private readonly floorPending = new Map<string, PendingFloor>();

  constructor(private readonly opts: ProtocolClientOptions) {
    this.now = opts.now ?? (() => Date.now());
    this.lastInboundAt = this.now();
    this.sessionId = opts.sessionId ?? randomSessionId();
    this.connect();

    const pingInterval = opts.pingIntervalMs ?? 2_500;
    const ping = setInterval(() => {
      // Liveness watchdog. A NAT or dead AP can drop a connection without a
      // FIN or RST: the socket reports OPEN while frames go into a black hole.
      // If nothing inbound has arrived across ~2.5 ping intervals, the
      // connection is presumed half-open and force-closed, which triggers the
      // reconnect path - where pending bids are re-sent under their original
      // idempotency keys. Exactly the sale-barn Wi-Fi failure mode.
      if (this.now() - this.lastInboundAt > pingInterval * 2.5) {
        this.forceReconnect('liveness watchdog');
        return;
      }
      this.sendPing();
    }, pingInterval);
    this.timers.push(ping);

    if (opts.role === 'clerk' || opts.role === 'spotter') {
      this.heartbeatTimer = setInterval(() => {
        this.sendRaw({ type: 'FLOOR_COMMAND', command: { type: 'HEARTBEAT' } });
      }, opts.heartbeatIntervalMs ?? 500);
      this.timers.push(this.heartbeatTimer);
    }

    // Brief section 31: an immediate health check when the tab comes back.
    // A backgrounded tab's timers are throttled and its socket may be long
    // dead; waiting for the watchdog costs seconds a bidder does not have.
    const doc = (globalThis as { document?: { addEventListener?: (t: string, l: () => void) => void; visibilityState?: string } }).document;
    if (doc && typeof doc.addEventListener === 'function') {
      this.onVisible = () => {
        if (doc.visibilityState !== 'visible' || this.closed || this.finalized) return;
        if (this.socket && this.socket.readyState === OPEN) {
          this.sendPing();
        } else if (this.reconnectTimer) {
          // A reconnect is scheduled for later; do it now.
          clearTimeout(this.reconnectTimer);
          this.reconnectTimer = null;
          this.connect();
        }
      };
      doc.addEventListener('visibilitychange', this.onVisible);
    }
  }

  /** The ring is sealed. Stop the floor heartbeat (the reducer would reject it,
   *  yielding one ERROR per beat) and stop reconnecting - there is nothing more
   *  to converge to. Idempotent. */
  private onFinalized(): void {
    if (this.finalized) return;
    this.finalized = true;
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  private sendPing(): void {
    this.sendRaw({ type: 'PING', clientTs: this.now(), ...(this.lastRttMs !== null ? { rttMs: this.lastRttMs } : {}) });
  }

  private emitHealth(): void {
    this.opts.onHealth?.(this.health);
  }

  /** Brief section 32. Facts only. */
  get health(): ClientConnectionHealth {
    return {
      websocketConnected: this.connected,
      lastHeartbeatAt: this.lastPongAt,
      lastRoundTripMs: this.lastRttMs,
      reconnectCount: this.reconnectCount,
      connectedDurationMs: this.connected && this.connectedAt !== null ? this.now() - this.connectedAt : 0,
      lastDisconnectReason: this.lastDisconnectReason,
      lastSnapshotAt: this.lastSnapshotAt,
      gapCount: this.gapCount,
      resyncing: this.resyncTimer !== null,
      ringLive: this.ringLive,
      ringMismatches: this.ringMismatches,
    };
  }

  /** ADR-70: the screen may be acted on - see ClientConnectionHealth.ringLive. */
  get ringLive(): boolean {
    return this.connected && this.helloSent && this.snapshotOnSocket && this.resyncTimer === null && !this.closed;
  }

  /** ADR-70: a frame naming another ring is dropped, counted and reported. */
  private wrongRing(msg: Record<string, unknown>): boolean {
    const ringId = msg['ringId'];
    if (typeof ringId !== 'string' || this.pinnedRing === null) return false;
    const saleId = msg['saleId'];
    if (ringId === this.pinnedRing.ringId && (typeof saleId !== 'string' || this.pinnedRing.saleId === null || saleId === this.pinnedRing.saleId)) return false;
    this.ringMismatches++;
    this.opts.onError?.(`RING_MISMATCH: this screen belongs to ring ${this.pinnedRing.ringId}${this.pinnedRing.saleId ? ' of sale ' + this.pinnedRing.saleId : ''}, and a frame named ${ringId}${typeof saleId === 'string' ? ' of sale ' + saleId : ''}; ignored`);
    this.emitHealth();
    return true;
  }

  private connect(): void {
    if (this.closed) return;
    this.reconnectTimer = null;
    this.opts.onStatus?.(this.lastSeq === 0 ? 'connecting' : 'reconnecting');

    const factory =
      this.opts.webSocketFactory ??
      ((url: string) => new (globalThis as { WebSocket: new (u: string) => WebSocketLike }).WebSocket(url));
    const socket = factory(this.opts.url);
    this.socket = socket;
    this.helloSent = false;
    this.snapshotOnSocket = false;

    socket.onopen = () => {
      this.connected = true;
      this.connectedAt = this.now();
      this.lastInboundAt = this.now();
      this.opts.onStatus?.('connected');
      void (async () => {
        const t = this.opts.token;
        const token = typeof t === 'function' ? await t() : t;
        this.sendRaw({
          type: 'HELLO',
          protocolVersion: PROTOCOL_VERSION,
          actorId: this.opts.actorId,
          role: this.opts.role,
          sessionId: this.sessionId,
          ...(token ? { token } : {}),
          ...(this.lastSeq > 0 ? { lastSeq: this.lastSeq } : {}),
          ...(this.opts.origin ? { origin: this.opts.origin } : {}),
        });
        if (this.socket !== socket || socket.readyState !== OPEN) return;
        this.helloSent = true;
        // Unresolved bids are re-sent with their original idempotency keys
        // AFTER HELLO (the server rejects bids from an un-HELLO'd socket); the
        // engine returns the original immutable outcome rather than reprocessing.
        for (const bid of this.pending.values()) this.sendRaw(bid.frame);
        // ADR-70: so are unanswered floor commands, under their original
        // client action ids. A command the ring already applied is answered
        // from its record (duplicate); one it never saw is judged now,
        // against the view it was pressed on - so a screen that went stale
        // while the socket was down is refused, never applied blind.
        for (const f of this.floorPending.values()) this.sendRaw(f.frame);
      })();
      this.emitHealth();
    };

    socket.onmessage = (ev) => {
      this.lastInboundAt = this.now();
      const msg = JSON.parse(String(ev.data)) as Record<string, unknown>;
      switch (msg['type']) {
        case 'SNAPSHOT': {
          if (this.wrongRing(msg)) return;
          if (this.pinnedRing === null && typeof msg['ringId'] === 'string') {
            this.pinnedRing = { ringId: msg['ringId'], saleId: typeof msg['saleId'] === 'string' ? msg['saleId'] : null };
          }
          this.commandResults = msg['commandResults'] === true;
          this.lastSeq = msg['seq'] as number;
          this.lastSnapshotAt = this.now();
          // The snapshot is the truth a RESYNC was waiting for - and, on a
          // fresh socket, what makes the screen safe to act on (ringLive).
          const wasLive = this.ringLive;
          // The ring answered: this connection works, so the next drop
          // starts the backoff over and sign-in failures are forgotten.
          if (!this.snapshotOnSocket) { this.backoffMs = 250; this.authFailures = 0; }
          this.snapshotOnSocket = true;
          if (this.resyncTimer) {
            clearTimeout(this.resyncTimer);
            this.resyncTimer = null;
          }
          if (!wasLive) this.emitHealth();
          this.opts.onSnapshot?.(msg as unknown as Snapshot);
          // The snapshot carries finality, so a reconnect/reload learns it too.
          if (msg['finalizedSeq'] != null) this.onFinalized();
          return;
        }
        case 'EVENT': {
          if (this.wrongRing(msg)) return;
          const seq = msg['seq'] as number;
          if (seq <= this.lastSeq) return; // duplicate or stale - strict order only
          // While a RESYNC is outstanding nothing is applied: the snapshot
          // that answers it supersedes every event in flight.
          if (this.resyncTimer) return;
          const prevSeq = msg['prevSeq'];
          if (typeof prevSeq === 'number' && this.lastSeq > 0 && prevSeq > this.lastSeq) {
            // A gap: this wire carried an event we never saw. Do not apply
            // partial truth - ask for the whole of it.
            this.gapCount++;
            this.resync('GAP');
            return;
          }
          this.lastSeq = seq;
          this.opts.onEvent?.(msg['event'] as Record<string, unknown>, seq);
          return;
        }
        case 'OUTCOME': {
          const outcome = msg as unknown as Outcome;
          const bid = this.pending.get(outcome.idempotencyKey);
          if (bid) {
            this.pending.delete(outcome.idempotencyKey);
            clearTimeout(bid.timer);
            clearTimeout(bid.nudge);
            bid.resolve(outcome);
          }
          return;
        }
        case 'COMMAND_RESULT': {
          // ADR-70: the ring's answer to one keyed floor command. A late
          // answer to a command that already settled TIMEOUT is dropped.
          const id = msg['clientActionId'];
          const f = typeof id === 'string' ? this.floorPending.get(id) : undefined;
          if (!f || typeof id !== 'string') return;
          this.floorPending.delete(id);
          clearTimeout(f.timer);
          const accepted = msg['accepted'] === true;
          f.resolve({
            status: accepted ? 'ACCEPTED' : 'REJECTED',
            accepted,
            commandType: typeof msg['commandType'] === 'string' ? msg['commandType'] : String(f.frame.command['type']),
            clientActionId: id,
            ...(typeof msg['reason'] === 'string' ? { reason: msg['reason'] } : {}),
            ...(typeof msg['seq'] === 'number' ? { seq: msg['seq'] } : {}),
            ...(msg['duplicate'] === true ? { duplicate: true } : {}),
            ...(typeof msg['authoritativeSeq'] === 'number' ? { authoritativeSeq: msg['authoritativeSeq'] } : {}),
          });
          return;
        }
        case 'YOU': {
          this.opts.onYou?.(msg);
          return;
        }
        case 'BID_RESOLUTION': {
          this.opts.onResolution?.(msg as unknown as BidResolution);
          return;
        }
        case 'MAX_BIDS': {
          if (this.wrongRing(msg)) return;
          this.opts.onMaxBids?.(msg as unknown as MaxBidsFrame);
          return;
        }
        case 'INTEREST': {
          if (this.wrongRing(msg)) return;
          this.opts.onInterest?.(msg as unknown as InterestFrame);
          return;
        }
        case 'SUPPORT': {
          void this.handleSupport(msg as unknown as SupportCommand);
          return;
        }
        case 'PONG': {
          const rtt = this.now() - (msg['clientTs'] as number);
          this.lastRttMs = rtt;
          this.lastPongAt = this.now();
          this.opts.onLatency?.(rtt);
          // ADR-60 cut 11: the ring's wire is ahead of what we applied and no
          // frame is coming to expose it - a trailing loss. Same answer as a
          // gap: do not guess, ask for the whole of it.
          const wireSeq = msg['lastSeq'];
          if (typeof wireSeq === 'number' && this.lastSeq > 0 && wireSeq > this.lastSeq && !this.resyncTimer) {
            this.gapCount++;
            this.resync('PONG');
            return;
          }
          this.emitHealth();
          return;
        }
        case 'ERROR': {
          const code = msg['code'];
          const rings = msg['rings'];
          const detail = typeof code === 'string'
            ? { code, ...(Array.isArray(rings) ? { rings: rings.filter((r): r is string => typeof r === 'string') } : {}) }
            : undefined;
          this.opts.onError?.(String(msg['message'] ?? 'error'), detail);
          return;
        }
        default:
          return;
      }
    };

    socket.onclose = (ev) => {
      const wasConnected = this.connected;
      this.connected = false;
      if (this.socket === socket) { this.helloSent = false; this.snapshotOnSocket = false; }
      const code = (ev as { code?: unknown } | undefined)?.code;
      this.lastDisconnectReason = this.closeReason ?? (typeof code === 'number' ? `close ${code}` : 'closed');
      this.closeReason = null;
      if (this.resyncTimer) {
        clearTimeout(this.resyncTimer);
        this.resyncTimer = null;
      }
      // A sealed ring has nothing left to converge to - don't reconnect.
      if (this.closed || this.finalized) {
        this.emitHealth();
        return;
      }
      // ADR-70 cut 3: closes a reconnect cannot fix end the client, said out
      // loud. 4400: this page speaks a protocol the ring no longer takes.
      // 4401: the ring refused the sign-in; a page that re-mints its token
      // gets a few tries (an expired token is the common, curable case), a
      // fixed or absent token would be refused the same way forever.
      const why = (ev as { reason?: unknown } | undefined)?.reason;
      const detail = typeof why === 'string' && why !== '' ? why : `close ${String(code)}`;
      if (code === 4400) {
        this.giveUp(`PROTOCOL_UNSUPPORTED: the ring does not accept this page's protocol (${detail}); reload the page`);
        return;
      }
      // ADR-70 cut 5f: the office assigned this staffer to another ring of the
      // sale. A fresh token is refused the same way, so stop now; the ERROR
      // frame before this close named the ring to go to.
      if (code === 4401 && why === RING_NOT_ASSIGNED_CLOSE_REASON) {
        this.giveUp('RING_NOT_ASSIGNED: this ring is not yours; open the ring the office assigned you');
        return;
      }
      if (code === 4401) {
        this.authFailures++;
        const limit = typeof this.opts.token === 'function' ? MAX_AUTH_RETRIES : 1;
        if (this.authFailures >= limit) {
          this.giveUp(`UNAUTHORIZED: the ring refused this sign-in (${detail}); sign in again`);
          return;
        }
      }
      if (wasConnected) this.reconnectCount++;
      this.opts.onStatus?.('reconnecting');
      this.emitHealth();
      // Jittered (0.5–1.5×): when one event disconnects the whole crowd - a
      // venue AP reboot, an NLB failover - synchronized retries arrive as
      // repeating SYN storms that collide forever; measured as 5,000 clients
      // stuck in equilibrium at ~2,000 connected. Jitter spreads the herd.
      this.reconnectTimer = setTimeout(() => this.connect(), this.backoffMs * (0.5 + Math.random()));
      // Cap at 2 s: the spec's reconnect-to-live-truth budget is 2 s, and a
      // client that waits longer than that is failing the budget by choice.
      this.backoffMs = Math.min(this.backoffMs * 2, 2_000);
    };

    socket.onerror = () => {
      this.closeReason = this.closeReason ?? 'socket error';
      try {
        socket.close();
      } catch {
        /* already closing */
      }
    };
  }

  /**
   * Ask the ring for a fresh authoritative snapshot and hold events until it
   * arrives (ADR-60). Public so a support command (REFRESH_RUNTIME_STATE) or a
   * UI "resync" control can use the same path the gap detector does. If the
   * ring does not answer inside `resyncTimeoutMs` the socket is presumed
   * half-open and reconnected, which yields a snapshot by itself.
   * ADR-70: `reason` tells the ring why (it counts resyncs by cause); the
   * start and the end of a resync both fire onHealth (ringLive flips).
   */
  resync(reason: ResyncReason = 'MANUAL'): void {
    if (this.closed || this.finalized) return;
    if (this.resyncTimer) return; // one outstanding at a time
    this.resyncTimer = setTimeout(() => {
      this.resyncTimer = null;
      this.forceReconnect('resync unanswered');
    }, this.opts.resyncTimeoutMs ?? 3_000);
    this.sendRaw({ type: 'RESYNC', lastSeq: this.lastSeq, reason });
    this.emitHealth();
  }

  private sendRaw(frame: unknown): void {
    if (this.socket && this.socket.readyState === OPEN) {
      this.socket.send(JSON.stringify(frame));
    }
  }

  /**
   * Place a bid at the ask the bidder saw. Resolves with the engine's outcome,
   * never silence: the promise survives reconnects (the bid is re-sent under
   * the same idempotency key) and rejects only after `bidTimeoutMs`.
   */
  bid(lotId: string, atAsk: number): Promise<Outcome> {
    const idempotencyKey = this.nextKey();
    const viewport = this.viewportNow();
    const frame = { type: 'PLACE_BID' as const, lotId, atAsk, idempotencyKey, ...(viewport ? { viewport } : {}) };
    return new Promise<Outcome>((resolve, reject) => {
      // Half-open detection specific to bids: a bid with no outcome inside the
      // nudge window forces a reconnect, which re-sends it. The engine's
      // idempotency makes this safe - the same key always returns the same
      // immutable outcome, so nudging can never double-bid.
      const nudge = setTimeout(() => {
        if (this.pending.has(idempotencyKey)) this.forceReconnect('bid unanswered');
      }, this.opts.bidNudgeMs ?? 3_000);
      const timer = setTimeout(() => {
        clearTimeout(nudge);
        this.pending.delete(idempotencyKey);
        reject(new Error(`bid ${idempotencyKey} unresolved after timeout`));
      }, this.opts.bidTimeoutMs ?? 15_000);
      this.pending.set(idempotencyKey, { frame, resolve, reject, timer, nudge });
      this.sendRaw(frame);
    });
  }

  /**
   * ADR-42: place a PRIVATE maximum on a lot. The engine bids for you at the ask,
   * only as far as needed; the amount never reaches any other client. Settles
   * with the engine's outcome like a bid (idempotent across reconnects).
   */
  placeMax(lotId: string, maxAmount: number): Promise<Outcome> {
    const viewport = this.viewportNow();
    return this.keyed({ type: 'PLACE_MAX_BID', lotId, maxAmount, ...(viewport ? { viewport } : {}) });
  }

  /** ADR-70 cut 6b: the window at the press, whole CSS pixels, or nothing.
   *  Read once per intent: a re-send after a reconnect carries the same. */
  private viewportNow(): { w: number; h: number } | undefined {
    try {
      const v = this.opts.viewport?.();
      if (!v) return undefined;
      const w = Math.round(v.w);
      const h = Math.round(v.h);
      return w >= 1 && h >= 1 && w <= 20_000 && h <= 20_000 ? { w, h } : undefined;
    } catch {
      return undefined;
    }
  }

  /** ADR-42: withdraw a standing max. Bids it already placed stand. */
  cancelMax(lotId: string): Promise<Outcome> {
    return this.keyed({ type: 'CANCEL_MAX_BID', lotId });
  }

  /** Send a keyed bidder intent with the same pending/nudge/timeout machinery as
   *  bid(): never silence, idempotent re-send after reconnect. */
  private keyed(frame: Record<string, unknown>): Promise<Outcome> {
    const idempotencyKey = this.nextKey();
    const full = { ...frame, idempotencyKey } as PendingBid['frame'];
    return new Promise<Outcome>((resolve, reject) => {
      const nudge = setTimeout(() => {
        if (this.pending.has(idempotencyKey)) this.forceReconnect('intent unanswered');
      }, this.opts.bidNudgeMs ?? 3_000);
      const timer = setTimeout(() => {
        clearTimeout(nudge);
        this.pending.delete(idempotencyKey);
        reject(new Error(`${String(frame['type'])} ${idempotencyKey} unresolved after timeout`));
      }, this.opts.bidTimeoutMs ?? 15_000);
      this.pending.set(idempotencyKey, { frame: full, resolve, reject, timer, nudge });
      this.sendRaw(full);
    });
  }

  /**
   * A keyed intent's idempotency key (production-gate review of cut 1, and
   * the post-merge audit). Two devices of the same bidder must never mint
   * the same key, even with the same clock value, the same counter and, by
   * mistake, the same session id: the key carries this client's session id
   * and 128 bits of randomness (a UUID v4), which is the same collision
   * budget the rest of the platform trusts for its identifiers.
   */
  private nextKey(): string {
    return `${this.opts.actorId}-${this.sessionId}-${this.bidCounter++}-${this.now()}-${randomSuffix()}`;
  }

  /** Idempotency keys of every intent still awaiting its outcome. The store
   *  shows SUBMITTING while this is non-empty. */
  get pendingKeys(): readonly string[] {
    return [...this.pending.keys()];
  }

  /**
   * The sanitized diagnostic bundle (brief section 43): connection facts,
   * runtime position, and whatever the page adds through `diagnostics()`.
   * Built from an allowlist - there is no way for a token or a card number
   * to end up here, because nothing reads them.
   */
  diagnosticBundle(): Record<string, unknown> {
    const h = this.health;
    const g = globalThis as { navigator?: { userAgent?: string; language?: string; onLine?: boolean }; screen?: { width?: number; height?: number }; devicePixelRatio?: number };
    return {
      capturedAt: this.now(),
      protocolVersion: PROTOCOL_VERSION,
      role: this.opts.role,
      sessionId: this.sessionId,
      lastSeq: this.lastSeq,
      connection: {
        websocketConnected: h.websocketConnected,
        lastRoundTripMs: h.lastRoundTripMs,
        reconnectCount: h.reconnectCount,
        connectedDurationMs: h.connectedDurationMs,
        lastDisconnectReason: h.lastDisconnectReason,
        lastSnapshotAt: h.lastSnapshotAt,
        gapCount: h.gapCount,
        resyncing: h.resyncing,
        pendingIntents: this.pending.size,
      },
      browser: {
        userAgent: g.navigator?.userAgent ?? null,
        language: g.navigator?.language ?? null,
        onLine: g.navigator?.onLine ?? null,
        screen: g.screen ? `${g.screen.width ?? 0}x${g.screen.height ?? 0}` : null,
        devicePixelRatio: g.devicePixelRatio ?? null,
      },
      ...(this.opts.diagnostics ? { app: this.opts.diagnostics() } : {}),
    };
  }

  private async handleSupport(cmd: SupportCommand): Promise<void> {
    // Acknowledge at once, then do the thing, then say how it went. Three
    // frames so support can see "received" separately from "done".
    this.sendRaw({ type: 'SUPPORT_RESULT', commandId: cmd.commandId, status: 'ACKNOWLEDGED' });
    let outcome: SupportOutcome;
    try {
      switch (cmd.command) {
        case 'REFRESH_RUNTIME_STATE':
          this.resync('SUPPORT');
          outcome = { status: 'SUCCEEDED', summary: 'resync requested' };
          break;
        case 'RECONNECT_REALTIME':
          // Answer first: the socket is about to go.
          this.sendRaw({ type: 'SUPPORT_RESULT', commandId: cmd.commandId, status: 'SUCCEEDED', summary: 'reconnecting' });
          this.forceReconnect('support: reconnect');
          return;
        case 'CAPTURE_CLIENT_DIAGNOSTICS':
        case 'REPORT_DEVICE_INFO':
        case 'RUN_NETWORK_DIAGNOSTIC':
          outcome = { status: 'SUCCEEDED', summary: 'diagnostics captured', diagnostics: this.diagnosticBundle() };
          break;
        default:
          outcome = this.opts.onSupport
            ? await this.opts.onSupport(cmd)
            : { status: 'FAILED', summary: `${cmd.command} is not supported by this client` };
      }
    } catch (err) {
      outcome = { status: 'FAILED', summary: err instanceof Error ? err.message : String(err) };
    }
    this.sendRaw({ type: 'SUPPORT_RESULT', commandId: cmd.commandId, status: outcome.status, ...(outcome.summary ? { summary: outcome.summary } : {}), ...(outcome.diagnostics ? { diagnostics: outcome.diagnostics } : {}) });
  }

  /** Tear down the current socket without ending the client; onclose schedules
   *  the reconnect, and pending bids are re-sent on reopen. */
  /** ADR-60 cut 8: drop the socket and reconnect through the normal backoff
   *  (a drill, the synthetic canary, a support command). */
  reconnect(reason = 'requested'): void {
    this.forceReconnect(reason);
  }

  private forceReconnect(reason: string): void {
    if (this.finalized) return; // sealed ring - nothing to reconnect for
    this.lastInboundAt = this.now(); // avoid an immediate second trigger
    this.closeReason = reason;
    try {
      this.socket?.close();
    } catch {
      /* already closing */
    }
  }

  /**
   * Send a floor command (clerk/spotter only; the server enforces the role).
   *
   * ADR-70: settles with a FloorCommandResult and never throws. On a ring
   * that answers floor commands (snapshot `commandResults`) the command goes
   * out under a fresh client action id and waits for the ring's
   * COMMAND_RESULT - across a reconnect it is re-sent under the same id, so
   * it is applied at most once - for up to `floorTimeoutMs`. It is refused
   * here, before the wire, when the screen cannot be trusted: no open
   * socket, no HELLO or no snapshot yet on this connection (NOT_CONNECTED),
   * or a resync outstanding (RESYNCING). Pausing the internet is always
   * allowed once the socket has said HELLO. An older ring gets the old
   * fire-and-forget frame and the promise settles SENT at once.
   */
  floor(command: Record<string, unknown>): Promise<FloorCommandResult> {
    const commandType = String(command['type']);
    const notSent = (reason: string): Promise<FloorCommandResult> => Promise.resolve({ status: 'NOT_SENT', accepted: false, commandType, clientActionId: null, reason });
    const alwaysSafe = commandType === 'PAUSE_ONLINE';
    if (this.closed || !this.connected || !this.helloSent || !this.socket || this.socket.readyState !== OPEN) return notSent('NOT_CONNECTED');
    if (!alwaysSafe && !this.snapshotOnSocket) return notSent('NOT_CONNECTED');
    if (!alwaysSafe && this.resyncTimer !== null) return notSent('RESYNCING');
    if (!this.commandResults) {
      this.sendRaw({ type: 'FLOOR_COMMAND', command });
      return Promise.resolve({ status: 'SENT', accepted: false, commandType, clientActionId: null });
    }
    const clientActionId = randomSuffix();
    const frame = { type: 'FLOOR_COMMAND' as const, clientActionId, command };
    return new Promise<FloorCommandResult>((resolve) => {
      const timer = setTimeout(() => {
        this.floorPending.delete(clientActionId);
        resolve({ status: 'TIMEOUT', accepted: false, commandType, clientActionId, reason: 'NO_ANSWER' });
      }, this.opts.floorTimeoutMs ?? 8_000);
      this.floorPending.set(clientActionId, { frame, resolve, timer });
      this.sendRaw(frame);
    });
  }

  /** ADR-70: client action ids of floor commands still awaiting the ring. */
  get pendingFloorIds(): readonly string[] {
    return [...this.floorPending.keys()];
  }

  /** ADR-60 cut 2: take the bidder's bidding lease for THIS device (spec 8.14).
   *  The ring answers every device of the bidder with a YOU frame. */
  claimSession(): void {
    this.sendRaw({ type: 'CLAIM_BIDDING_SESSION' });
  }

  /** ADR-60 cut 3: the bidder has read the material updates on `lotId` (null =
   *  sale-wide) through `throughSeq`. The ring answers with a YOU frame. */
  acknowledge(lotId: string | null, throughSeq: number): void {
    this.sendRaw({ type: 'ACKNOWLEDGE_ANNOUNCEMENTS', lotId, throughSeq });
  }

  /** ADR-70 cut 6f (hitlist 10): what this bidder is about to do on the lot
   *  in the ring, for the auctioneer's Online interest panel. Fire and
   *  forget, never queued: a signal from before a reconnect means nothing
   *  after it. A bidder socket only, and the same signal for the same lot
   *  goes at most once a second. The page decides whether to call this at
   *  all from the snapshot's `intentShown`. Returns whether it went. */
  intent(lotId: string, signal: BidderIntentSignal): boolean {
    if (this.opts.role !== 'bidder' || this.closed || !this.connected || !this.helloSent) return false;
    if (!this.socket || this.socket.readyState !== OPEN) return false;
    const key = lotId + '|' + signal;
    const at = this.now();
    if (this.lastIntent !== null && this.lastIntent.key === key && at - this.lastIntent.at < 1_000) return false;
    this.lastIntent = { key, at };
    this.sendRaw({ type: 'INTENT', lotId, signal });
    return true;
  }
  private lastIntent: { key: string; at: number } | null = null;

  /** ADR-60 cut 6: the media watchdog's report (brief section 38). Fire and
   *  forget on the bidding socket; it never affects bidding, and a socket
   *  that is down simply drops it (the next one says the same). */
  reportMedia(report: Record<string, unknown>): boolean {
    // The three required facts (protocol MediaReport); a report without them
    // would only come back as a malformed-frame ERROR.
    if (typeof report['playerSessionStatus'] !== 'string' || typeof report['streamStatus'] !== 'string' || typeof report['videoRendering'] !== 'boolean') return false;
    this.sendRaw({ type: 'MEDIA_REPORT', ...report });
    return true;
  }

  get currentSeq(): number {
    return this.lastSeq;
  }

  /** The ring closed the socket for a reason a reconnect cannot fix: say why,
   *  then end the client (status 'closed', pending work answered). */
  private giveUp(message: string): void {
    this.lastDisconnectReason = message;
    this.opts.onError?.(message);
    this.close();
    this.emitHealth();
  }

  close(): void {
    this.closed = true;
    for (const t of this.timers) clearInterval(t);
    if (this.resyncTimer) clearTimeout(this.resyncTimer);
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    const doc = (globalThis as { document?: { removeEventListener?: (t: string, l: () => void) => void } }).document;
    if (this.onVisible && doc && typeof doc.removeEventListener === 'function') doc.removeEventListener('visibilitychange', this.onVisible);
    for (const bid of this.pending.values()) {
      clearTimeout(bid.timer);
      clearTimeout(bid.nudge);
      bid.reject(new Error('client closed'));
    }
    this.pending.clear();
    for (const [id, f] of this.floorPending) {
      clearTimeout(f.timer);
      f.resolve({ status: 'TIMEOUT', accepted: false, commandType: String(f.frame.command['type']), clientActionId: id, reason: 'CLIENT_CLOSED' });
    }
    this.floorPending.clear();
    this.socket?.close();
    this.opts.onStatus?.('closed');
  }
}
